import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard, Users, FileText, Briefcase, Receipt, Settings, LogOut, Menu, X, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const navItems = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
  { label: 'Clients', icon: Users, path: '/clients' },
  { label: 'Proposals', icon: FileText, path: '/proposals' },
  { label: 'Projects', icon: Briefcase, path: '/projects' },
  { label: 'Invoices', icon: Receipt, path: '/invoices' },
  { label: 'Settings', icon: Settings, path: '/settings' },
];

// Map path segments to readable labels
const segmentLabels = {
  dashboard: 'Dashboard',
  clients: 'Clients',
  proposals: 'Proposals',
  projects: 'Projects',
  invoices: 'Invoices',
  settings: 'Settings',
  new: 'New',
  edit: 'Edit',
};

function Breadcrumbs() {
  const location = useLocation();
  const segments = location.pathname.split('/').filter(Boolean);

  if (segments.length <= 1) return null; // Don't show breadcrumbs on top-level pages

  const crumbs = segments.map((seg, i) => {
    const path = '/' + segments.slice(0, i + 1).join('/');
    const isLast = i === segments.length - 1;
    // Use label map, or capitalize the segment, or show "Detail" for IDs
    const label = segmentLabels[seg] || (seg.match(/^[a-f0-9-]+$/i) ? 'Detail' : seg.charAt(0).toUpperCase() + seg.slice(1));

    return (
      <span key={path} className="flex items-center gap-1.5">
        <ChevronRight className="w-3.5 h-3.5 text-ink-subtle" strokeWidth={1.5} />
        {isLast ? (
          <span className="text-small text-ink">{label}</span>
        ) : (
          <Link to={path} className="text-small text-ink-subtle hover:text-forest transition-colors">{label}</Link>
        )}
      </span>
    );
  });

  return (
    <div className="flex items-center gap-1 mb-4 -mt-1" data-testid="breadcrumbs">
      <Link to="/dashboard" className="text-small text-ink-subtle hover:text-forest transition-colors">Home</Link>
      {crumbs}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const currentPath = location.pathname;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-6 border-b border-line">
        <Link to="/" className="flex items-center gap-2.5" data-testid="sidebar-logo">
          <img src="/logo192.png" alt="Flowlancer" className="w-8 h-8 rounded-md" />
          <span className="font-display text-subhead text-ink">FlowLancer</span>
        </Link>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1">
        {navItems.map((item) => {
          const isActive = currentPath === item.path || currentPath.startsWith(item.path + '/');
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-small font-medium transition-colors
                ${isActive
                  ? 'bg-forest-tint text-forest'
                  : 'text-ink-muted hover:text-ink hover:bg-wash'
                }`}
            >
              <item.icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t border-line">
        <div className="flex items-center gap-3 px-2 mb-3">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-wash flex items-center justify-center text-ink-muted text-small font-medium">
              {(user?.name || 'U')[0]}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-small font-medium text-ink truncate">{user?.name || 'User'}</p>
            <p className="text-micro text-ink-muted truncate normal-case tracking-normal">{user?.email || ''}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full justify-start text-ink-muted hover:text-destructive hover:bg-wash"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-paper">
      {/* Mobile header */}
      <div className="lg:hidden sticky top-0 z-50 flex items-center justify-between px-4 py-3 border-b border-line bg-paper/90 backdrop-blur-xl">
        <button onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
          <Menu className="w-5 h-5 text-ink" strokeWidth={1.5} />
        </button>
        <span className="font-display text-ink">FlowLancer</span>
        <div className="w-5" />
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-ink/40" onClick={() => setSidebarOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-60 bg-paper border-r border-line">
            <button className="absolute right-3 top-3 p-1" onClick={() => setSidebarOpen(false)}>
              <X className="w-5 h-5 text-ink-subtle" strokeWidth={1.5} />
            </button>
            <SidebarContent />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className="fixed left-0 top-0 z-40 h-screen w-60 border-r border-line bg-paper hidden lg:block" data-testid="sidebar">
        <SidebarContent />
      </aside>

      {/* Main content */}
      <main className="lg:ml-60">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
          <Breadcrumbs />
          {children}
        </div>
      </main>
    </div>
  );
}
